import React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Briefcase, Users, FileBarChart, Building } from "lucide-react";
import { RoadmapCard } from "./roadmap/RoadmapCard";
import { FeatureHighlight } from "./roadmap/FeatureHighlight";
import { CTASection } from "./roadmap/CTASection";

const Roadmap = () => {
  const isMobile = useIsMobile();

  return (
    <section id="roadmap" className="py-20 bg-white relative">
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-savyi-purple/5 to-transparent -z-10" />
      
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-gradient">Roadmap</span> Sneak Peek
          </h2>
          <p className="text-gray-600 text-lg">
            An innovative financial journey awaits
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <RoadmapCard
            status="Epic Launch"
            title="Mood-based Budgets"
            description="Stressed? AI pauses non-essentials. Happy? Maybe it's time for that small splurge you've been saving for. Your financial plan that adapts to your emotional state."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                <line x1="9" x2="9.01" y1="9" y2="9" />
                <line x1="15" x2="15.01" y1="9" y2="9" />
              </svg>
            }
          />
          
          <RoadmapCard
            status="Game Changer"
            title="AR Debt Visualizer"
            description="Project your progress onto your wall with augmented reality. Watch your debt shrink and savings grow in real-time. Makes your financial progress tangible and motivating."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 9V6a2 2 0 0 0-2-2H9" />
                <path d="M3 16v3a2 2 0 0 0 4 2h10" />
                <path d="M21 9H3" />
                <path d="M3 16h9" />
                <path d="M13.2 22l3.8-3.8" />
                <path d="M13.2 2l3.8 3.8" />
              </svg>
            }
          />
          
          <RoadmapCard
            status="Money Revolution"
            title="AI Negotiator"
            description="'Savyi vs. Comcast' billing disputes. Let our AI handle the frustrating negotiations with service providers, creditors, and bill collectors for the best possible outcomes."
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
                <path d="M2 7h20" />
                <path d="M22 7v3a2 2 0 0 1-2 2v0a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12v0a2 2 0 0 1-2-2V7" />
              </svg>
            }
          />
          
          <RoadmapCard
            status="Power Move"
            title="Financial Advisor Integration"
            description="Connect directly with certified financial advisors who can provide personalized guidance and recommendations tailored to your unique financial situation."
            icon={<Briefcase className="h-6 w-6" />}
          />
          
          <RoadmapCard
            status="Money Multiverse"
            title="Investment Partnerships"
            description="Access exclusive investment opportunities through our strategic partnerships with leading investment firms, brokerages, and financial institutions."
            icon={<FileBarChart className="h-6 w-6" />}
          />
          
          <RoadmapCard
            status="Ultimate Level-Up"
            title="White Labeling for Advisors"
            description="Financial advisors can customize Savyi with their branding to offer clients a powerful financial management tool that strengthens advisor-client relationships."
            icon={<Building className="h-6 w-6" />}
          />
        </div>
        
        <div className="mt-16 pt-8 border-t border-gray-100">
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">
              Key Features You'll Rock!
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These are just some of the exciting features we're building to make your financial journey more engaging and effective
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureHighlight
              title="Smart Savings & Goal Tracking"
              description="Track savings goals with visual aids and get AI recommendations to reach them faster."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4" />
                  <path d="M20 12h-4" />
                  <path d="M12 18v4" />
                  <path d="M4 12h4" />
                  <circle cx="12" cy="12" r="10" />
                  <circle cx="12" cy="12" r="4" />
                </svg>
              }
            />
            
            <FeatureHighlight
              title="24/7 AI Financial Assistant"
              description="Chat with your personal financial advisor anytime for real-time guidance and answers."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                </svg>
              }
            />
            
            <FeatureHighlight
              title="Secure Cloud Integration"
              description="Your data stays with you through secure integration with your preferred cloud storage."
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 16.7a5 5 0 0 0-8.9-4L10 4a1 1 0 0 0-2 0v10.3a5 5 0 1 0 7.5 6.1" />
                  <line x1="8" x2="8" y1="14" y2="21" />
                  <line x1="16" x2="16" y1="9" y2="1" />
                </svg>
              }
            />
            
            <FeatureHighlight
              title="Debt & Savings Strategies"
              description="AI-powered strategies to eliminate debt and accelerate savings based on your financial situation."
              icon={<TrendingUp className="h-6 w-6" />}
            />
          </div>
          
          <div className="text-center mt-12">
            <Link to="/timeline" className="inline-flex items-center group">
              <span className="text-lg font-medium text-savyi-purple group-hover:text-savyi-purple/80 transition-colors">
                View the full financial journey roadmap
              </span>
              <ArrowRight className="ml-2 h-5 w-5 text-savyi-purple group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
        
        <CTASection />
      </div>
    </section>
  );
};

export default Roadmap;
